/**
 * AskGranulloDock.jsx — UNIVERSAL INTELLIGENCE COMPOSER
 *
 * Persistent ambient cognitive entry point across all authenticated routes.
 * CMD/CTRL+K to open. ESC to collapse. SHIFT+ENTER for multiline.
 *
 * States:
 * - collapsed: floating pill dock (bottom-right)
 * - expanded: side panel (right) with thread + composer
 *
 * Features:
 * - Route-aware contextual suggestions
 * - Thread memory (last thread per workspace)
 * - Deep-linked context objects
 * - Loading phase indicators (real system cognition)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles, X, Send, Loader2, ChevronRight,
  Shield, GitBranch, Layers, TrendingUp, Zap, Eye,
  AlertTriangle, CheckCircle2, FileText, RotateCcw,
  Target, Lock, ArrowRight, Clock, Minimize2,
  BookOpen, Scale, XCircle, BarChart3,
} from 'lucide-react';
import './AskGranulloDock.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══ ROUTE-AWARE SUGGESTIONS + MICRO-CONTEXT ═══ */
const ROUTE_SUGGESTIONS = {
  default: [
    "What would this have blocked?",
    "Where does accountability actually break here?",
    "What evidence would a partner need to see?",
    "What breaks first in a pilot?",
  ],
  '/control-room': [
    "What's the current risk exposure telling us?",
    "Why were these payouts blocked today?",
    "Where's the authority bottleneck right now?",
    "What would a compliance summary look like?",
  ],
  '/labs': [
    "What would a misuse simulation reveal here?",
    "How much drift are we seeing across adapters?",
    "What parameters would stress this the most?",
    "What breaks if we relax this rule?",
  ],
  '/payment-twins': [
    "What's the lineage behind this twin?",
    "What does replay memory show for this flow?",
    "What structural fixes have worked on similar twins?",
  ],
  '/granullo-atlas': [
    "What's the exposure topology showing right now?",
    "Where's commitment concentration risk highest?",
    "How does regional governance readiness compare?",
  ],
  '/glass-inspector': [
    "What's the full lineage on this case?",
    "What structural fix was applied and why?",
    "What does the evidence chain tell us?",
  ],
  '/play/rail-partner': [
    "What would our rail actually need to return here?",
    "Where does reconciliation become provable?",
    "What changes if callback timing is delayed?",
  ],
  '/play/sponsor': [
    "What exactly gets blocked in this workflow?",
    "How would audit read this later?",
    "Where does sponsor control remain human?",
  ],
  '/play/investor': [
    "Why can't rails build this themselves?",
    "What compounds every time usage increases?",
    "Where does switching cost actually emerge?",
  ],
  '/play/regulator': [
    "What survives independent audit here?",
    "Where does policy history become provable?",
    "How is this different from surveillance?",
  ],
  '/play/agentic-commerce': [
    "What gets escalated instead of auto-approved?",
    "How does drift become visible before spend?",
    "What makes the agent accountable later?",
  ],
  '/play/cbdc-benefits': [
    "How do public-intent boundaries stay enforceable?",
    "What exactly gets blocked at merchant level?",
    "How is dignity preserved while enforcing policy?",
  ],
};

const ROUTE_MICRO_CONTEXT = {
  '/control-room': 'Control Room',
  '/labs': 'Labs',
  '/payment-twins': 'Payment Twins',
  '/granullo-atlas': 'Atlas',
  '/glass-inspector': 'Glass Inspector',
  '/dashboard': 'Dashboard',
  '/play/rail-partner': 'Play — Rail Partner',
  '/play/sponsor': 'Play — Sponsor',
  '/play/investor': 'Play — Investor',
  '/play/regulator': 'Play — Regulator',
  '/play/agentic-commerce': 'Play — Agentic Commerce',
  '/play/cbdc-benefits': 'Play — CBDC & Benefits',
  '/programs': 'Programs',
  '/transactions': 'Transactions',
  '/approvals': 'Approvals',
  '/intelligence': 'Intelligence',
  '/exposure-map': 'Exposure Map',
  '/governance': 'Governance',
  '/compliance': 'Compliance Hub',
  '/audit': 'Audit',
  '/marketplace': 'Marketplace',
};

/* ═══ CONTEXT OBJECT DEEP LINK MAP ═══ */
const DEEP_LINK_MAP = {
  rule_pack:        { route: '/control-room',   label: 'Control Room',    icon: Shield },
  payment_twin:     { route: '/payment-twins',  label: 'Payment Twins',   icon: GitBranch },
  authority_chain:  { route: '/control-room',   label: 'Authority Chain',  icon: Layers },
  blocked_payout:   { route: '/control-room',   label: 'Blocked Payout',   icon: XCircle },
  drift_signal:     { route: '/labs',           label: 'Labs',            icon: TrendingUp },
  misuse_future:    { route: '/labs',           label: 'Misuse Future',   icon: AlertTriangle },
  structural_fix:   { route: '/control-room',   label: 'Structural Fix',  icon: Zap },
  audit_memo:       { route: '/compliance-hub', label: 'Compliance Hub',  icon: FileText },
  partner_incident: { route: '/partner-os',     label: 'Partner OS',      icon: BookOpen },
  labs_experiment:  { route: '/labs',           label: 'Labs Experiment', icon: Target },
  atlas_exposure:   { route: '/atlas',          label: 'Atlas',           icon: BarChart3 },
  replay_trace:     { route: '/control-room',   label: 'Replay Memory',   icon: RotateCcw },
  beneficiary:      { route: '/control-room',   label: 'Beneficiary',     icon: Eye },
  sponsor:          { route: '/partner-os',     label: 'Sponsor',         icon: BookOpen },
  evidence:         { route: '/compliance-hub', label: 'Evidence',        icon: FileText },
};

/* BarChart3 now imported above with other lucide-react icons */

/* ═══ DRAFT ACTION TYPES ═══ */
const DRAFT_TYPE_MAP = {
  'suggest_fix': 'draft_rule',
  'create_rule': 'draft_rule',
  'authority_reroute': 'draft_authority_reroute',
  'labs_experiment': 'create_labs_experiment',
  'misuse_simulation': 'generate_misuse_simulation',
  'atlas_review': 'open_atlas_review',
  'decision_memo': 'create_decision_memo',
  'partner_incident': 'create_partner_incident',
  'control_room_task': 'create_control_room_task',
};

/* ═══ PROACTIVE ICON MAP ═══ */
const PROACTIVE_ICONS = {
  'recurring_misuse': AlertTriangle,
  'authority_bottleneck': Lock,
  'blocked_pattern_recall': RotateCcw,
  'decision_drift': TrendingUp,
  'labs_experiment_trigger': Zap,
  'atlas_exposure_review': Eye,
  'route_specific_risk': Target,
};

/* ═══ LOADING PHASES ═══ */
const PHASES = [
  'Reading context',
  'Pulling evidence',
  'Checking governance trail',
  'Thinking through implications',
  'Forming response',
];

/* ═══ CONTEXT OBJECT CARD (DEEP-LINKABLE) ═══ */
function DockContextObject({ obj, navigate, threadId }) {
  const link = DEEP_LINK_MAP[obj.type];
  const Icon = link?.icon || Eye;

  const handleClick = () => {
    if (link) {
      // Store return breadcrumb (local + backend)
      const breadcrumbData = {
        threadId,
        returnPath: window.location.pathname,
        objectType: obj.type,
        objectLabel: obj.label,
      };
      sessionStorage.setItem('intel_breadcrumb', JSON.stringify(breadcrumbData));
      // Persist to Memory Spine
      fetch(`${API_URL}/api/intelligence/threads/breadcrumb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread_id: threadId,
          return_path: window.location.pathname,
          object_type: obj.type,
          object_label: obj.label,
          target_route: `${link.route}?from=intelligence&obj=${obj.type}&ref=${encodeURIComponent(obj.id || obj.label)}`,
        }),
      }).catch(() => {});
      navigate(`${link.route}?from=intelligence&obj=${obj.type}&ref=${encodeURIComponent(obj.id || obj.label)}`);
    }
  };

  return (
    <div
      className="dock-context-obj"
      onClick={handleClick}
      data-testid={`dock-context-${obj.type}`}
    >
      <div className="dock-context-header">
        <Icon size={11} />
        <span className="dock-context-type">{obj.type?.replace(/_/g, ' ')}</span>
        {obj.status && (
          <span className={`dock-context-status ${obj.status}`}>{obj.status}</span>
        )}
      </div>
      <p className="dock-context-label">{obj.label}</p>
      {link && (
        <span className="dock-context-link">
          Open in {link.label} <ArrowRight size={9} />
        </span>
      )}
    </div>
  );
}

/* ═══ REASONING BLOCK ═══ */
function DockReasoningBlock({ block }) {
  const icons = { analysis: Eye, evidence: GitBranch, rule_check: Shield, simulation: TrendingUp, recommendation: Zap, warning: AlertTriangle };
  const Icon = icons[block.type] || Eye;

  return (
    <div className={`dock-reasoning ${block.type || ''}`}>
      <div className="dock-reasoning-head">
        <Icon size={11} />
        <span>{block.type?.replace(/_/g, ' ')}</span>
        {block.confidence != null && (
          <span className="dock-confidence">{Math.round(block.confidence * 100)}%</span>
        )}
      </div>
      <p className="dock-reasoning-title">{block.title}</p>
      <p className="dock-reasoning-text">{block.content}</p>
    </div>
  );
}

/* ═══ SUGGESTED ACTION (DRAFT PIPELINE) ═══ */
function DockAction({ action, sessionId, threadId }) {
  const [applied, setApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleApply = async () => {
    setLoading(true);
    try {
      const draftType = DRAFT_TYPE_MAP[action.type] || 'create_decision_memo';
      await fetch(`${API_URL}/api/intelligence/drafts/pipeline/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread_id: threadId || sessionId,
          draft_type: draftType,
          title: action.action?.slice(0, 100) || 'Intelligence recommendation',
          description: action.action || '',
          source_query: action.source_query || '',
          route_context: window.location.pathname,
        }),
      });
      setApplied(true);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  return (
    <div className="dock-action">
      <p className="dock-action-text">{action.action}</p>
      <button
        className={`dock-action-btn ${applied ? 'applied' : ''}`}
        onClick={handleApply}
        disabled={loading || applied}
        data-testid="dock-apply-action"
      >
        {loading ? <Loader2 size={10} className="animate-spin" /> :
         applied ? <><CheckCircle2 size={10} /> Draft created</> :
         <><Send size={10} /> Create draft</>}
      </button>
    </div>
  );
}

/* ═══ PROACTIVE SUGGESTION CARD ═══ */
function ProactiveSuggestionCard({ suggestion, onAsk }) {
  const Icon = PROACTIVE_ICONS[suggestion.suggestion_type] || AlertTriangle;
  const severityColor = {
    critical: 'var(--granullo-editorial-red, #CD2200)',
    high: 'var(--hero-rebel-red, #CD2200)',
    medium: 'var(--g-accent, #B8D7F0)',
  }[suggestion.severity] || 'var(--g-muted)';

  return (
    <div
      className={`dock-proactive-card ${suggestion.severity}`}
      data-testid={`proactive-${suggestion.suggestion_type}`}
    >
      <div className="dock-proactive-header">
        <Icon size={12} style={{ color: severityColor }} />
        <span className="dock-proactive-severity">{suggestion.severity}</span>
      </div>
      <p className="dock-proactive-title">{suggestion.title}</p>
      <p className="dock-proactive-summary">{suggestion.summary}</p>
      {suggestion.recommended_action && (
        <button
          className="dock-proactive-action"
          onClick={() => onAsk(suggestion.summary)}
          data-testid="proactive-ask"
        >
          <ChevronRight size={10} /> Investigate
        </button>
      )}
    </div>
  );
}


/* ═══ MAIN DOCK COMPONENT ═══ */

export default function AskGranulloDock() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [thread, setThread] = useState([]); // Array of {role, content, response}
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [threadRestored, setThreadRestored] = useState(false);
  const [proactiveSuggestions, setProactiveSuggestions] = useState([]);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [pulseVisible, setPulseVisible] = useState(false);
  const [showProactive, setShowProactive] = useState(false);
  const [sessionId] = useState(() => {
    const saved = sessionStorage.getItem('intel_session_id');
    return saved || crypto.randomUUID();
  });

  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const threadEndRef = useRef(null);

  // Persist session ID
  useEffect(() => {
    sessionStorage.setItem('intel_session_id', sessionId);
  }, [sessionId]);

  // Initialize thread: try backend first, fallback to sessionStorage
  useEffect(() => {
    let cancelled = false;
    async function loadThread() {
      try {
        const res = await fetch(`${API_URL}/api/intelligence/threads/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data.thread?.exchanges?.length > 0) {
            const restored = data.thread.exchanges.map(ex => ([
              { role: 'user', content: ex.question },
              { role: 'assistant', content: ex.response?.summary || 'No response', response: ex.response || {}, queryClass: ex.query_class },
            ])).flat();
            setThread(restored);
            setThreadRestored(true);
            return;
          }
        }
      } catch {}
      // Fallback: sessionStorage
      const saved = sessionStorage.getItem(`intel_thread_${sessionId}`);
      if (saved && !cancelled) {
        try { setThread(JSON.parse(saved)); } catch {}
      }
      if (!cancelled) setThreadRestored(true);
    }
    loadThread();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Save thread to sessionStorage (fast local cache)
  useEffect(() => {
    if (thread.length > 0) {
      sessionStorage.setItem(`intel_thread_${sessionId}`, JSON.stringify(thread));
    }
  }, [thread, sessionId]);

  // Ensure backend thread exists on first open
  useEffect(() => {
    if (open && threadRestored) {
      fetch(`${API_URL}/api/intelligence/threads/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          thread_id: sessionId,
          workspace_id: 'default',
          route_context: location.pathname,
        }),
      }).catch(() => {});
    }
  }, [open, threadRestored, sessionId, location.pathname]);

  // Proactive intelligence polling (every 30s + on route change)
  useEffect(() => {
    let cancelled = false;
    async function fetchProactive() {
      try {
        const res = await fetch(
          `${API_URL}/api/intelligence/proactive/suggestions?workspace_id=default&route=${encodeURIComponent(location.pathname)}`
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          setProactiveSuggestions(data.suggestions || []);
          if (data.should_pulse && data.suggestions.length > 0) {
            setShouldPulse(true);
            setPulseVisible(true);
          }
        }
      } catch {}
    }
    fetchProactive();
    const interval = setInterval(fetchProactive, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [location.pathname]);

  // Ambient pulse: fade after 3 cycles (8.4s) if dock not opened
  useEffect(() => {
    if (pulseVisible && !open) {
      const timer = setTimeout(() => setPulseVisible(false), 8400);
      return () => clearTimeout(timer);
    }
    if (open) {
      setPulseVisible(false);
      setShouldPulse(false);
    }
  }, [pulseVisible, open]);

  // ═══ REAL-TIME TRUTH BINDING (SSE) ═══
  const [liveEvents, setLiveEvents] = useState([]);
  const [streamConnected, setStreamConnected] = useState(false);
  const [trustScore, setTrustScore] = useState(null);
  const [trustFactors, setTrustFactors] = useState([]);
  const [degradedProviders, setDegradedProviders] = useState([]);
  const eventSourceRef = useRef(null);
  const reconnectTimer = useRef(null);
  const processedEvents = useRef(new Set()); // dedup guard

  // SSE connection with reconnect resilience
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;

    function connect() {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      try {
        const es = new EventSource(
          `${API_URL}/api/intelligence/stream/events?workspace_id=default`
        );

        es.onopen = () => {
          setStreamConnected(true);
          retryCount = 0;
        };

        es.onmessage = (evt) => {
          try {
            const event = JSON.parse(evt.data);
            // Dedup guard — prevent duplicate processing
            if (event.event_id && processedEvents.current.has(event.event_id)) return;
            if (event.event_id) processedEvents.current.add(event.event_id);
            // Cap dedup set size
            if (processedEvents.current.size > 200) {
              const arr = [...processedEvents.current];
              processedEvents.current = new Set(arr.slice(-100));
            }

            setLiveEvents(prev => [...prev.slice(-20), event]);

            // Trigger pulse only for material events
            if (event.pulse) {
              setShouldPulse(true);
              setPulseVisible(true);
            }
          } catch {}
        };

        es.onerror = () => {
          setStreamConnected(false);
          es.close();
          // Reconnect with exponential backoff
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
            reconnectTimer.current = setTimeout(connect, delay);
          }
        };

        eventSourceRef.current = es;
      } catch {
        setStreamConnected(false);
      }
    }

    connect();

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, []);

  // Trust decay assessment (on thread change + periodic)
  useEffect(() => {
    if (!sessionId || thread.length === 0) return;
    let cancelled = false;

    async function assessTrust() {
      try {
        // Get federation health first
        const healthRes = await fetch(`${API_URL}/api/intelligence/federation/health`);
        const healthData = healthRes.ok ? await healthRes.json() : null;

        if (healthData) {
          const degraded = (healthData.providers || []).filter(p =>
            p.status === 'degraded' || p.status === 'offline' || p.status === 'error'
          );
          if (!cancelled) setDegradedProviders(degraded);
        }

        const res = await fetch(`${API_URL}/api/intelligence/trust/assess`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            thread_id: sessionId,
            provider_health: healthData,
          }),
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTrustScore(data.trust_score);
          setTrustFactors(data.factors || []);
        }
      } catch {}
    }

    assessTrust();
    const interval = setInterval(assessTrust, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [sessionId, thread.length]);

  // CMD/CTRL+K shortcut — dock takes priority via capture phase + stopPropagation
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler, true); // capture phase
    return () => window.removeEventListener('keydown', handler, true);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Auto-scroll thread
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread, isLoading]);

  // Route-aware suggestions
  const suggestions = ROUTE_SUGGESTIONS[location.pathname] || ROUTE_SUGGESTIONS.default;

  // Route micro-context for collapsed pill
  const microContext = Object.entries(ROUTE_MICRO_CONTEXT).find(
    ([path]) => location.pathname.startsWith(path)
  )?.[1] || null;

  const submitQuery = useCallback(async (q) => {
    const question = q || query.trim();
    if (!question) return;
    setQuery('');
    setIsLoading(true);
    setLoadingPhase(0);

    // Add user message to thread
    setThread(prev => [...prev, { role: 'user', content: question }]);

    const phaseTimer = setInterval(() => {
      setLoadingPhase(prev => Math.min(prev + 1, PHASES.length - 1));
    }, 800);

    try {
      const res = await fetch(`${API_URL}/api/intelligence/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, session_id: sessionId }),
      });
      const data = await res.json();
      clearInterval(phaseTimer);

      const assistantMsg = {
        role: 'assistant',
        content: data.response?.summary || 'No response',
        response: data.response || {},
        queryClass: data.query_class,
      };
      setThread(prev => [...prev, assistantMsg]);

      // Persist exchange to Memory Spine (retry-safe, fire-and-forget)
      const persistExchange = async (retries = 2) => {
        for (let i = 0; i <= retries; i++) {
          try {
            await fetch(`${API_URL}/api/intelligence/threads/exchange`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                thread_id: sessionId,
                question,
                response: data.response || {},
                query_class: data.query_class,
                confidence: data.response?.confidence,
                referenced_objects: data.response?.context_objects?.slice(0, 10),
                route_context: location.pathname,
              }),
            });
            break; // Success
          } catch (e) {
            if (i === retries) { /* silent fail after retries */ }
            else await new Promise(r => setTimeout(r, 1000 * (i + 1)));
          }
        }
      };
      persistExchange();
    } catch (err) {
      clearInterval(phaseTimer);
      setThread(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message}`,
        response: { uncertainty_flags: [err.message] },
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [query, sessionId, location.pathname]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitQuery();
    }
  };

  // Breadcrumb return
  const breadcrumb = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('intel_breadcrumb') || 'null');
    } catch { return null; }
  })();

  const returnToThread = () => {
    sessionStorage.removeItem('intel_breadcrumb');
    setOpen(true);
  };

  return (
    <>
      {/* Breadcrumb return banner */}
      {breadcrumb && !open && (
        <motion.button
          className="dock-breadcrumb"
          onClick={returnToThread}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          data-testid="dock-breadcrumb-return"
        >
          <ArrowRight size={12} style={{ transform: 'rotate(180deg)' }} />
          Return to Intelligence thread
        </motion.button>
      )}

      {/* Collapsed pill */}
      <AnimatePresence>
        {!open && (
          <motion.button
            className={`dock-pill ${pulseVisible ? 'dock-pill-pulse' : ''}`}
            onClick={() => setOpen(true)}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            whileHover={{ scale: 1.04 }}
            data-testid="dock-pill"
          >
            <Sparkles size={14} />
            <div className="dock-pill-content">
              <span className="dock-pill-label">Ask Granullo</span>
              {microContext && (
                <span className="dock-pill-context">{microContext}</span>
              )}
            </div>
            <span className="dock-pill-shortcut">
              {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded side panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              className="dock-backdrop"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Panel */}
            <motion.div
              className="dock-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              data-testid="dock-panel"
            >
              {/* Header */}
              <div className="dock-header">
                <div className="dock-header-left">
                  <Sparkles size={14} />
                  <span>Granullo Intelligence</span>
                </div>
                <div className="dock-header-actions">
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                    onPointerDown={(e) => { e.stopPropagation(); }}
                    className="dock-close"
                    data-testid="dock-close"
                  >
                    <Minimize2 size={14} />
                  </button>
                </div>
              </div>

              {/* Thread */}
              <div className="dock-thread" data-testid="dock-thread">
                {thread.length === 0 && !isLoading && (
                  <div className="dock-empty">
                    <p className="dock-empty-title">What do you want to understand?</p>
                    <p className="dock-empty-desc">
                      I can reason over live programs, rules, blocked payouts, authority chains, and governance evidence — ask me anything.
                    </p>

                    {/* Proactive suggestions (Phase C) */}
                    {proactiveSuggestions.length > 0 && (
                      <div className="dock-proactive-section" data-testid="dock-proactive-section">
                        <button
                          className="dock-proactive-toggle"
                          onClick={() => setShowProactive(p => !p)}
                          data-testid="dock-proactive-toggle"
                        >
                          <AlertTriangle size={11} />
                          <span>{proactiveSuggestions.length} intelligence signal{proactiveSuggestions.length !== 1 ? 's' : ''}</span>
                          <ChevronRight size={10} style={{ transform: showProactive ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                        </button>
                        {showProactive && (
                          <div className="dock-proactive-list">
                            {proactiveSuggestions.slice(0, 5).map((s, i) => (
                              <ProactiveSuggestionCard
                                key={i}
                                suggestion={s}
                                onAsk={submitQuery}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="dock-suggestions">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          className="dock-suggestion"
                          onClick={() => submitQuery(s)}
                          data-testid={`dock-suggestion-${i}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trust decay + live status indicators */}
                {(trustScore !== null || degradedProviders.length > 0 || liveEvents.length > 0) && thread.length > 0 && (
                  <div className="dock-status-bar" data-testid="dock-status-bar">
                    {!streamConnected && (
                      <div className="dock-status-item degraded" data-testid="dock-stream-degraded">
                        <AlertTriangle size={9} />
                        <span>Live stream disconnected — using cached context</span>
                      </div>
                    )}
                    {degradedProviders.length > 0 && (
                      <div className="dock-status-item degraded" data-testid="dock-provider-degraded">
                        <Layers size={9} />
                        <span>Degraded: {degradedProviders.map(p => p.label).join(', ')}</span>
                      </div>
                    )}
                    {trustScore !== null && trustScore < 0.85 && (
                      <div className={`dock-status-item ${trustScore < 0.5 ? 'critical' : 'warn'}`} data-testid="dock-trust-decay">
                        <Scale size={9} />
                        <span>Trust: {Math.round(trustScore * 100)}%{trustFactors.length > 0 ? ` — ${trustFactors[0].explanation.slice(0, 60)}` : ''}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Thread restored indicator */}
                {thread.length > 0 && threadRestored && (
                  <div className="dock-thread-resume" data-testid="dock-thread-resume">
                    <Clock size={10} />
                    <span>Thread resumed from {microContext || 'previous session'}</span>
                  </div>
                )}

                {thread.map((msg, i) => (
                  <div key={i} className={`dock-msg ${msg.role}`}>
                    {msg.role === 'user' ? (
                      <div className="dock-msg-user">
                        <p>{msg.content}</p>
                      </div>
                    ) : (
                      <div className="dock-msg-assistant">
                        <p className="dock-msg-summary">{msg.content}</p>

                        {/* Reasoning blocks */}
                        {msg.response?.reasoning_blocks?.slice(0, 3).map((b, j) => (
                          <DockReasoningBlock key={j} block={b} />
                        ))}

                        {/* Context objects */}
                        {msg.response?.context_objects?.length > 0 && (
                          <div className="dock-context-grid">
                            {msg.response.context_objects.slice(0, 4).map((obj, j) => (
                              <DockContextObject
                                key={j}
                                obj={obj}
                                navigate={navigate}
                                threadId={sessionId}
                              />
                            ))}
                          </div>
                        )}

                        {/* Uncertainty */}
                        {msg.response?.uncertainty_flags?.length > 0 && (
                          <div className="dock-uncertainty">
                            {msg.response.uncertainty_flags.slice(0, 2).map((f, j) => (
                              <div key={j} className="dock-uncertainty-item">
                                <AlertTriangle size={10} />
                                <span>{f}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        {msg.response?.suggested_actions?.slice(0, 2).map((a, j) => (
                          <DockAction key={j} action={a} sessionId={sessionId} threadId={sessionId} />
                        ))}

                        {/* Follow-up prompt */}
                        {msg.response?.context_objects?.length > 0 && (
                          <button
                            className="dock-followup"
                            onClick={() => {
                              const obj = msg.response.context_objects[0];
                              submitQuery(`Tell me more about ${obj.type?.replace(/_/g, ' ')}: ${obj.label}`);
                            }}
                            data-testid="dock-followup"
                          >
                            <ChevronRight size={10} />
                            Ask follow-up on {msg.response.context_objects[0]?.type?.replace(/_/g, ' ')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Loading */}
                {isLoading && (
                  <div className="dock-loading">
                    {PHASES.map((phase, i) => (
                      <div key={i} className={`dock-loading-phase ${i <= loadingPhase ? 'active' : ''}`}>
                        {i < loadingPhase ? <CheckCircle2 size={10} /> :
                         i === loadingPhase ? <Loader2 size={10} className="animate-spin" /> :
                         <span className="dock-phase-dot" />}
                        <span>{phase}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div ref={threadEndRef} />
              </div>

              {/* Composer */}
              <div className="dock-composer" data-testid="dock-composer">
                <textarea
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={suggestions[0]}
                  className="dock-composer-input"
                  rows={1}
                  data-testid="dock-composer-input"
                />
                <button
                  className="dock-composer-send"
                  onClick={() => submitQuery()}
                  disabled={!query.trim() || isLoading}
                  data-testid="dock-composer-send"
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
